import { type Monitor, Signal, batch } from "@suse-o11y/signal";

import type { TableColOrder } from "~/components/table/col-order";
import { ImmutableMap } from "~/tools/immutable-map";
import { ImmutableSet } from "~/tools/immutable-set";

/**
 * Determines whether a label should be visible based on the selection state and visibility mode.
 *
 * @param label - The label to check for visibility
 * @param isSelectedVisible - When `true`, selected labels are visible and unselected are hidden.
 *                            When `false`, selected labels are hidden and unselected are visible.
 * @param selectedLabels - Set of labels that have been explicitly selected
 *
 * @returns `true` if the label should be visible, `false` otherwise
 *
 * @remarks
 * The visibility logic follows these rules:
 * - If {@link isSelectedVisible} is `false` and label is not in {@link selectedLabels}: visible (not explicitly hidden)
 * - If {@link isSelectedVisible} is `false` and label is in {@link selectedLabels}: hidden (explicitly hidden)
 * - If {@link isSelectedVisible} is `true` and label is not in {@link selectedLabels}: hidden (not explicitly shown)
 * - If {@link isSelectedVisible} is `true` and label is in {@link selectedLabels}: visible (explicitly shown)
 */
function isLabelVisible(label: string, isSelectedVisible: boolean, selectedLabels: ImmutableSet<string>): boolean {
	return isSelectedVisible === selectedLabels.has(label);
}

function isOnlyVisibleLabel(
	label: string,
	labels: ReadonlyArray<string>,
	isSelectedVisible: boolean,
	selectedLabels: ImmutableSet<string>,
): boolean {
	// When selectedLabels represents visible labels, it must be the only one in the set
	if (isSelectedVisible) {
		return selectedLabels.size === 1 && selectedLabels.has(label);
	}

	// When selectedLabels represents hidden labels, all but the target label must be in the set
	return selectedLabels.size === labels.length - 1 && !selectedLabels.has(label);
}

export class State {
	/**
	 * When true, the {@link selectedLabels} stores VISIBLE labels,
	 * when false, the {@link selectedLabels} stores HIDDEN labels.
	 *
	 * @example
	 * isSelectedVisible = true
	 * selectedLabels = ["label1", "label2"]
	 * => visible labels are "label1" and "label2", the rest is hidden
	 *
	 * @example
	 * isSelectedVisible = false
	 * selectedLabels = ["label1", "label2"]
	 * => hidden labels are "label1" and "label2", the rest is visible
	 *
	 * @example
	 * isSelectedVisible = true
	 * selectedLabels = []
	 * => all labels are hidden
	 *
	 * @example
	 * isSelectedVisible = false
	 * selectedLabels = []
	 * => all labels are visible
	 */
	private readonly isSelectedVisible = Signal(false);

	private readonly selectedLabels = Signal<ImmutableSet<string>>(ImmutableSet.empty);

	public readonly tableColumnsOrder = Signal(ImmutableMap.empty<string, TableColOrder>());

	public isLabelVisible(monitor: Monitor, label: string): boolean {
		return isLabelVisible(label, this.isSelectedVisible.read(monitor), this.selectedLabels.read(monitor));
	}

	public setAllLabels(visible: boolean): void {
		batch(() => {
			/**
			 * When {@link visible} is true, make the {@link selectedLabels} to store hidden labels with {@link isSelectedVisible} = false,
			 * and another way around.
			 */
			this.isSelectedVisible.write(!visible);
			this.selectedLabels.write((selectedLabels) => selectedLabels.clear());
		});
	}

	public setLabel(label: string, visible: boolean): void {
		this.selectedLabels.write((selectedLabels, monitor) => {
			if (this.isSelectedVisible.read(monitor) === visible) {
				return selectedLabels.add(label);
			}

			return selectedLabels.delete(label);
		});
	}

	public toggleLabel(label: string): void {
		this.selectedLabels.write((selectedLabels) => selectedLabels.toggle(label));
	}

	public focusLabel(label: string, labels: ReadonlyArray<string>): void {
		batch((monitor) => {
			const isSelectedVisible = this.isSelectedVisible.read(monitor);
			const selectedLabels = this.selectedLabels.read(monitor);

			// The only case to select all labels is when the target label is the only visible one at the moment
			if (isOnlyVisibleLabel(label, labels, isSelectedVisible, selectedLabels)) {
				this.setAllLabels(true);
			} else {
				this.isSelectedVisible.write(true);
				this.selectedLabels.write(ImmutableSet.fromArray([label]));
			}
		});
	}
}
